import {
  Component,
  inject,
  Input,
  OnChanges,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import { Media } from 'src/app/models/page-components/media.model';
import { SanitizeHtmlPipe } from 'src/app/pipes/safe-html.pipe';
import { CookieConsentService } from 'src/app/services/cookie-consent.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-inner-html',
  imports: [SanitizeHtmlPipe],
  templateUrl: './inner-html.component.html',
  styleUrls: ['./inner-html.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class InnerHtmlComponent implements OnChanges {
  router = inject(Router);
  cookieConsentService = inject(CookieConsentService);

  @Input() html?: string = '';
  @Input() center: boolean = false;
  @Input() allowLinks: boolean = true;
  @Input() fromDate?: Date;
  @Input() toDate?: Date;

  apiDomain = environment.apiDomain;

  ngOnChanges(): void {
    if (!this.html) return;

    const imgTags = this.html.match(/<img [^>]*src="[^"]*"[^>]*>/gm);

    if (imgTags) {
      for (let i = 0; i < imgTags.length; i++) {
        const element = imgTags[i];

        if (element) {
          let source = element
            .match(/src="[^"]*"[^>]/gm)
            ?.toString()
            .split('"')[1];

          const elementClass = element
            .match(/class="[^"]*"[^>]/gm)
            ?.toString()
            .split('"')[1];

          const elementCaption = element
            .match(/alt="[^"]*"[^>]/gm)
            ?.toString()
            .split('"')[1];

          if (source) {
            let image = new Media();

            if (source.includes('http')) return;

            image.Url = source
              ?.replace(this.apiDomain, '')
              .replace('upload/editor/', '')
              .replace('editor/', '');
            image.ImageRatio = 1;

            const newUrl =
              environment.imgFxDomain
                .replace('{mode}', 'cdn')
                .replace('size-{size}/', 'size-10x10/') + source;

            this.html = this.html.replace(source, newUrl);
          }
        }
      }
    }

    // Find all links with href = #cookieconsent, and add an onclick event to them that calls the cookie consent service to open the cookie consent settings
    const linkTags = this.html.match(/<a [^>]*href="cookieconsent"[^>]*>/gm);
    if (linkTags) {
      for (let i = 0; i < linkTags.length; i++) {
        const element = linkTags[i];
        if (element) {
          const newElement = element.replace(
            'href="cookieconsent"',
            'href="#cookieconsent"',
          );
          this.html = this.html.replace(element, newElement);
        }
      }
    }
  }

  processLinks(e: any) {
    const anchor = (e.target as HTMLElement).closest('a');
    if (!anchor) return;

    e.preventDefault();
    const link = anchor.getAttribute('href') ?? '';
    if (!link || link === '') return;

    if (link === '#cookieconsent') {
      this.cookieConsentService.showConsent.set(true);
      return;
    }

    const samePageHash = this.getSamePageHash(link);
    if (samePageHash) {
      this.scrollToAnchor(samePageHash);
      return;
    }

    if (link.startsWith('#')) {
      this.scrollToAnchor(link);
      return;
    }

    if (link.includes('mailto:')) {
      window.location.href = link;
      return;
    }
    if (link?.substr(0, 4) != 'http') {
      this.router.navigateByUrl(link.replace('..', ''));
      return;
    }
    if (
      anchor.getAttribute('target') &&
      anchor.getAttribute('target') === '_blank'
    ) {
      window.open(link, '_blank');
    } else {
      this.router.navigate([link]);
    }

    e.preventDefault(false);
  }

  private scrollToAnchor(hash: string): void {
    if (typeof window === 'undefined') return;

    const id = decodeURIComponent(hash.substring(1));
    const target = document.getElementById(id);

    if (!target) return;

    window.history.pushState(null, '', hash);
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private getSamePageHash(link: string): string | null {
    if (typeof window === 'undefined') return null;

    try {
      const url = new URL(link, window.location.href);

      if (
        url.origin !== window.location.origin ||
        !url.hash ||
        this.normalizePath(url.pathname) !==
          this.normalizePath(window.location.pathname)
      ) {
        return null;
      }

      return url.hash;
    } catch {
      return null;
    }
  }

  private normalizePath(path: string): string {
    const normalizedPath = path.replace(/\/+$/, '');

    return normalizedPath || '/';
  }

  extractUrlValue(url: string, key: string) {
    var match = url.match(`${key}=([^&]+)`);
    return match ? match[1] : null;
  }

  replaceAll(html: string, find: string, replace: string) {
    return html.replace(new RegExp(find, 'g'), replace);
  }
}
